import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { remediationQueries, triageQueries, vendorIssueQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, topBtn } from './VendorDetails';
import RequestRemediationModal from '../Remediation/RequestRemediationModal';

type Severity = 'Critical' | 'High' | 'Medium';
type Status = 'new' | 'worsened' | 'improved' | 'persisting' | null;

interface HistoryPoint { scan_id: string; date: string; severity: Severity | null }

interface Issue {
  name: string;
  category: string | null;
  severity: Severity;
  previous_severity: Severity | null;
  status: Status;
  score_impact: number | null;
  description: string | null;
  recommendation: string | null;
  evidence: unknown;
  first_detected: string;
  first_detected_is_lower_bound: boolean;
  seen_in: number;
  history: HistoryPoint[];
}

interface ResolvedIssue {
  name: string;
  category: string | null;
  previous_severity: Severity;
  current_severity: string | null;
  previous_score_impact: number | null;
  current_score_impact: number | null;
  description: string | null;
  recommendation: string | null;
}

interface Payload {
  has_scan: boolean;
  scan_id?: string;
  scan_date?: string;
  previous_scan_id?: string | null;
  previous_scan_date?: string | null;
  scans_considered?: number;
  issues: Issue[];
  resolved: ResolvedIssue[];
}

type View = 'open' | 'new' | 'resolved';
type SortKey = 'severity' | 'name' | 'category' | 'impact' | 'first';
type Selection = { kind: 'issue' | 'resolved'; name: string };

interface Props {
  vendorId: string;
  vendorName: string;
  vendorDomain?: string | null;
  isDark: boolean;
  // Opens a remediation request in this vendor's own Remediation tab.
  onOpenRemediation: (requestId: string) => void;
}

const SEVERITIES: Severity[] = ['Critical', 'High', 'Medium'];
const SEV_RANK: Record<Severity, number> = { Critical: 0, High: 1, Medium: 2 };
const SEV_STYLE: Record<Severity, { light: string; dark: string; bgLight: string; bgDark: string }> = {
  Critical: { light: '#DC2626', dark: '#f87171', bgLight: '#FEF2F2', bgDark: '#2a1414' },
  High: { light: '#EA580C', dark: '#fb923c', bgLight: '#FFF7ED', bgDark: '#2a1a10' },
  Medium: { light: '#D97706', dark: '#fbbf24', bgLight: '#FFFBEB', bgDark: '#2a2114' },
};
const STATUS_LABEL: Record<Exclude<Status, null>, string> = {
  new: 'New', worsened: 'Worsened', improved: 'Improved', persisting: 'Persisting',
};

const APP_HEADER_HEIGHT = 64;
const PANEL_SLIDE_MS = 220;
const MONO = "'JetBrains Mono', ui-monospace, monospace";

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};

const fmtDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtShortDate = (value: string) => {
  const d = new Date(value);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};
const fmtNumber = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};
const fmtImpact = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '—';
  if (value === 0) return '0';
  return value > 0 ? `+${fmtNumber(value)}` : `−${fmtNumber(Math.abs(value))}`;
};
const recovered = (r: ResolvedIssue) => Math.abs(r.previous_score_impact || 0) - Math.abs(r.current_score_impact || 0);

// Rule-based descriptions explain the check generically and then give the verdict behind an emoji marker
// ("🔴 HIGH RISK: 31 ... detected."); AI-written descriptions open with the finding itself. Either way one
// sentence of it is the summary. The marker is required so plain labels like "NOTE:" are not taken for a verdict.
const VERDICT = /[^\w\s.,;:'"()-]{1,3}\s*[A-Z][A-Z ]{2,}:\s*([^]+)/;
const firstSentence = (text: string) => text.split(/(?<=[.!?])\s/)[0].trim();
function summarize(issue: { description: string | null }): string {
  const description = (issue.description || '').trim();
  const verdict = description.match(VERDICT);
  return firstSentence(verdict ? verdict[1] : description);
}

function evidenceFacts(evidence: unknown): [string, string][] {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return [];
  const facts: [string, string][] = [];
  for (const [key, value] of Object.entries(evidence as Record<string, unknown>)) {
    if (value === null || value === undefined || value === '') continue;
    const label = key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
    if (['string', 'number', 'boolean'].includes(typeof value)) {
      facts.push([label, String(value)]);
    } else if (Array.isArray(value)) {
      if (!value.length) continue;
      const simple = value.every(v => ['string', 'number'].includes(typeof v));
      facts.push([label, simple && value.length <= 4 ? value.join(', ') : `${value.length} item${value.length === 1 ? '' : 's'}`]);
    }
    if (facts.length === 8) break;
  }
  return facts;
}

const csvCell = (value: unknown) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
const downloadCsv = (filename: string, header: string[], rows: unknown[][]) => {
  const csv = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function VendorIssues({ vendorId, vendorName, vendorDomain, isDark, onOpenRemediation }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canAct = !!user && user.role !== 'viewer';

  const line = isDark ? '#1f1f1f' : T.bdr;
  const ink = isDark ? '#e5e7eb' : T.ink;
  const slate = isDark ? '#94a3b8' : T.slate;
  const muted = isDark ? '#64748b' : T.muted;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const rowLine = isDark ? '#161616' : '#F1F5F9';
  const accent = isDark ? '#60A5FA' : T.blue;
  const sevColor = (s: Severity) => (isDark ? SEV_STYLE[s].dark : SEV_STYLE[s].light);
  const sevBg = (s: Severity) => (isDark ? SEV_STYLE[s].bgDark : SEV_STYLE[s].bgLight);
  const statusStyle = (status: Exclude<Status, null> | 'resolved') => ({
    new: { bg: isDark ? '#0c1d33' : '#EFF6FF', color: isDark ? '#93c5fd' : '#1D4ED8' },
    worsened: { bg: isDark ? '#2a1414' : '#FEF2F2', color: isDark ? '#f87171' : '#B91C1C' },
    improved: { bg: isDark ? '#132a1b' : '#F0FDF4', color: isDark ? '#4ade80' : '#15803D' },
    resolved: { bg: isDark ? '#132a1b' : '#F0FDF4', color: isDark ? '#4ade80' : '#15803D' },
    persisting: { bg: isDark ? '#1a1a1a' : '#F1F5F9', color: slate },
  }[status]);

  const [view, setView] = useState<View>('open');
  // Tiles are highlighted only after the user picks one, never by default.
  const [pickedTile, setPickedTile] = useState<'open' | Severity | 'new' | 'resolved' | null>(null);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<Severity | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'category'>('none');
  const [sortKey, setSortKey] = useState<SortKey>('severity');
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const panelTimer = useRef<number | undefined>(undefined);
  const deepLinkHandled = useRef(false);

  const issuesQuery = useQuery(vendorIssueQueries.issues(vendorId));
  const data = (issuesQuery.data as Payload | undefined) ?? null;
  const inRemediation = useQuery(remediationQueries.vendorRisks(vendorId)).data ?? {};
  // Issues ticked for a remediation request (kept while switching views; names are stable across scans).
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [requesting, setRequesting] = useState<string[] | null>(null);
  // Set when "Request remediation" is opened from the AI triage banner below, so the modal can be
  // pre-filled with the agent's draft title/message/due date and resolve the suggestion on send.
  const [fromSuggestion, setFromSuggestion] = useState<{ id: string; title: string; summary: string | null; dueDate: string | null } | null>(null);
  const suggestionQuery = useQuery(triageQueries.forVendor(vendorId));
  const suggestion = suggestionQuery.data ?? null;
  const dismissSuggestion = useMutation({
    mutationFn: (id: string) => api.post(`/v1/triage-suggestions/${id}/dismiss`),
    meta: { invalidates: [['triage-suggestions']] },
  });
  const issues = useMemo(() => data?.issues ?? [], [data]);
  const resolved = useMemo(() => data?.resolved ?? [], [data]);
  const hasComparison = !!data?.previous_scan_id;

  const newIssues = useMemo(() => issues.filter(i => i.status === 'new' || i.status === 'worsened'), [issues]);
  const viewIssues = view === 'new' ? newIssues : issues;

  const categories = useMemo(() => {
    const source = view === 'resolved' ? resolved.map(r => r.category) : viewIssues.map(i => i.category);
    return Array.from(new Set(source.filter(Boolean) as string[])).sort();
  }, [view, resolved, viewIssues]);

  const query = search.trim().toLowerCase();
  const matches = (name: string, category: string | null, severity: Severity) =>
    (!severityFilter || severity === severityFilter) &&
    (!categoryFilter || category === categoryFilter) &&
    (!query || name.toLowerCase().includes(query) || (category || '').toLowerCase().includes(query));

  const severityCounts = useMemo(() => {
    const counts: Record<Severity, number> = { Critical: 0, High: 0, Medium: 0 };
    if (view === 'resolved') resolved.forEach(r => { counts[r.previous_severity] += 1; });
    else viewIssues.forEach(i => { counts[i.severity] += 1; });
    return counts;
  }, [view, resolved, viewIssues]);

  const sortedIssues = useMemo(() => {
    const rows = viewIssues.filter(i => matches(i.name, i.category, i.severity));
    const dir = sortAsc ? 1 : -1;
    const bySeverity = (a: Issue, b: Issue) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || Math.abs(b.score_impact || 0) - Math.abs(a.score_impact || 0);
    const compare: Record<SortKey, (a: Issue, b: Issue) => number> = {
      severity: (a, b) => dir * bySeverity(a, b),
      name: (a, b) => dir * a.name.localeCompare(b.name),
      category: (a, b) => dir * (a.category || '').localeCompare(b.category || '') || bySeverity(a, b),
      impact: (a, b) => dir * (Math.abs(b.score_impact || 0) - Math.abs(a.score_impact || 0)),
      first: (a, b) => dir * (new Date(a.first_detected).getTime() - new Date(b.first_detected).getTime()) || bySeverity(a, b),
    };
    return rows.sort(compare[sortKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIssues, query, severityFilter, categoryFilter, sortKey, sortAsc]);

  const sortedResolved = useMemo(() => {
    const rows = resolved.filter(r => matches(r.name, r.category, r.previous_severity));
    const dir = sortAsc ? 1 : -1;
    const bySeverity = (a: ResolvedIssue, b: ResolvedIssue) => SEV_RANK[a.previous_severity] - SEV_RANK[b.previous_severity] || recovered(b) - recovered(a);
    const compare: Record<SortKey, (a: ResolvedIssue, b: ResolvedIssue) => number> = {
      severity: (a, b) => dir * bySeverity(a, b),
      name: (a, b) => dir * a.name.localeCompare(b.name),
      category: (a, b) => dir * (a.category || '').localeCompare(b.category || '') || bySeverity(a, b),
      impact: (a, b) => dir * (recovered(b) - recovered(a)),
      first: (a, b) => dir * bySeverity(a, b),
    };
    return rows.sort(compare[sortKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, query, severityFilter, categoryFilter, sortKey, sortAsc]);

  const rowCount = view === 'resolved' ? sortedResolved.length : sortedIssues.length;
  const grouped = groupBy === 'category';
  const totalPages = grouped ? 1 : Math.max(1, Math.ceil(rowCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageSlice = <R,>(rows: R[]) => (grouped ? rows : rows.slice((currentPage - 1) * pageSize, currentPage * pageSize));

  const selectedIssue = selection?.kind === 'issue' ? issues.find(i => i.name === selection.name) ?? null : null;
  const selectedResolved = selection?.kind === 'resolved' ? resolved.find(r => r.name === selection.name) ?? null : null;

  const openPanel = (next: Selection) => {
    window.clearTimeout(panelTimer.current);
    setSelection(next);
    setPanelVisible(true);
  };
  const closePanel = () => {
    setPanelVisible(false);
    window.clearTimeout(panelTimer.current);
    panelTimer.current = window.setTimeout(() => setSelection(null), PANEL_SLIDE_MS);
  };
  useEffect(() => () => window.clearTimeout(panelTimer.current), []);

  // A copied issue link (?tab=issues&issue=<name>) opens that issue once the data is here.
  useEffect(() => {
    if (deepLinkHandled.current || !data) return;
    deepLinkHandled.current = true;
    const name = searchParams.get('issue');
    if (!name) return;
    if (issues.some(i => i.name === name)) openPanel({ kind: 'issue', name });
    else if (resolved.some(r => r.name === name)) { setView('resolved'); openPanel({ kind: 'resolved', name }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const resetPaging = () => setPage(1);
  const changeView = (next: View) => {
    setView(next);
    setCategoryFilter('');
    resetPaging();
    if (selection) closePanel();
  };
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
    resetPaging();
  };
  const clearFilters = () => { setSearch(''); setSeverityFilter(''); setCategoryFilter(''); resetPaging(); };
  const filtersActive = !!(query || severityFilter || categoryFilter);

  const exportView = () => {
    const date = new Date().toISOString().slice(0, 10);
    const file = `${slug(vendorName)}-issues-${view}-${date}.csv`;
    if (view === 'resolved') {
      downloadCsv(file, ['Issue', 'Category', 'Was', 'Now', 'Impact recovered', 'Resolved in scan'],
        sortedResolved.map(r => [r.name, r.category, r.previous_severity, r.current_severity ?? 'No longer reported', fmtNumber(recovered(r)), fmtDate(data?.scan_date)]));
    } else {
      downloadCsv(file, ['Severity', 'Issue', 'Category', 'Score impact', 'Status', 'First detected', 'Seen in scans', 'Summary', 'Recommendation'],
        sortedIssues.map(i => [i.severity, i.name, i.category, i.score_impact ?? '', i.status ? STATUS_LABEL[i.status] : '', fmtDate(i.first_detected),
          `${i.seen_in} of ${data?.scans_considered ?? i.seen_in}`, summarize(i), i.recommendation]));
    }
  };

  /* ── Loading / error / empty ─────────────────────────────────── */

  if (!data && (issuesQuery.isPending || issuesQuery.isFetching)) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ ...card(isDark), height: 84 }} />)}
        </div>
        <div style={{ ...card(isDark), height: 360 }} />
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ ...card(isDark), padding: 32, textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 6 }}>Couldn't load issues</div>
        <div style={{ fontSize: 13, color: slate, marginBottom: 14 }}>{apiError(issuesQuery.error, 'Something went wrong.')}</div>
        <button onClick={() => issuesQuery.refetch()} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Try again</button>
      </div>
    );
  }
  if (!data.has_scan) {
    return (
      <div style={{ ...card(isDark), padding: '48px 24px', textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 4 }}>No completed scan yet</div>
        <div style={{ fontSize: 13, color: slate }}>Issues appear here once a scan of this vendor finishes.</div>
      </div>
    );
  }

  /* ── KPI tiles ───────────────────────────────────────────────── */

  const openCounts: Record<Severity, number> = { Critical: 0, High: 0, Medium: 0 };
  issues.forEach(i => { openCounts[i.severity] += 1; });
  const topCategoryNote = (severity: Severity) => {
    const tally = new Map<string, number>();
    issues.filter(i => i.severity === severity).forEach(i => tally.set(i.category || 'Other', (tally.get(i.category || 'Other') || 0) + 1));
    const top = Array.from(tally.entries()).sort((a, b) => b[1] - a[1])[0];
    if (!top) return 'None open';
    return top[1] === openCounts[severity] ? `All in ${top[0]}` : `${top[1]} in ${top[0]}`;
  };
  const worsenedCount = issues.filter(i => i.status === 'worsened').length;
  const recoveredTotal = resolved.reduce((sum, r) => sum + recovered(r), 0);

  const kpiStyle = (active: boolean, color?: string): React.CSSProperties => ({
    ...card(isDark), padding: '12px 14px', cursor: 'pointer', textAlign: 'left', fontFamily: sans,
    boxShadow: active ? `inset 0 0 0 1.5px ${color || ink}` : 'none',
  });
  const kpiLabel: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted };
  const kpiValue = (color: string): React.CSSProperties => ({ fontSize: 22, fontWeight: 700, color, marginTop: 6, lineHeight: 1 });
  const kpiNote: React.CSSProperties = { fontSize: 11, color: slate, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  type TileKey = NonNullable<typeof pickedTile>;
  // A picked tile stays highlighted only while the list still shows what it selected; changing the view or
  // filters by other means drops the highlight.
  const tileActive = (key: TileKey) => pickedTile === key && (
    key === 'open' ? view === 'open' && !severityFilter
      : key === 'new' || key === 'resolved' ? view === key
        : view === 'open' && severityFilter === key
  );
  const pickTile = (key: TileKey) => {
    if (tileActive(key)) {
      setPickedTile(null);
      if (key !== 'open' && key !== 'new' && key !== 'resolved') { setSeverityFilter(''); resetPaging(); }
      return;
    }
    setPickedTile(key);
    if (key === 'new' || key === 'resolved') { changeView(key); return; }
    if (view !== 'open') changeView('open');
    setSeverityFilter(key === 'open' ? '' : key);
    resetPaging();
  };

  /* ── Table pieces ────────────────────────────────────────────── */

  const th: React.CSSProperties = {
    textAlign: 'left', fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted,
    padding: '9px 14px', background: surface2, borderBottom: `1px solid ${line}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { fontSize: 12, padding: '11px 14px', borderBottom: `1px solid ${rowLine}`, verticalAlign: 'middle', color: ink };
  // Capped to the viewport so a long one-line summary cannot push the table wider than the card.
  const summaryStyle: React.CSSProperties = {
    fontSize: 11, color: muted, marginTop: 2, maxWidth: 'min(420px, 28vw)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  const sortHeader = (key: SortKey, label: string, width?: number) => (
    <th style={{ ...th, width, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(key)}>
      {label}{sortKey === key ? (sortAsc ? ' ↓' : ' ↑') : ''}
    </th>
  );
  const SeverityCell = ({ severity }: { severity: Severity }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, color: sevColor(severity) }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: sevColor(severity) }} />{severity}
    </span>
  );
  const StatusPill = ({ status }: { status: Exclude<Status, null> | 'resolved' }) => {
    const style = statusStyle(status);
    return (
      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, whiteSpace: 'nowrap', background: style.bg, color: style.color }}>
        {status === 'resolved' ? 'Resolved' : STATUS_LABEL[status]}
      </span>
    );
  };
  const rowBg = (name: string, kind: Selection['kind']) =>
    selection?.name === name && selection.kind === kind && panelVisible ? (isDark ? '#0c1d33' : '#EFF6FF') : undefined;

  const issueRow = (issue: Issue) => (
    <tr key={issue.name} data-issue-row onClick={() => openPanel({ kind: 'issue', name: issue.name })}
      style={{ cursor: 'pointer', background: rowBg(issue.name, 'issue') }}
      onMouseEnter={e => { if (!rowBg(issue.name, 'issue')) e.currentTarget.style.background = surface2; }}
      onMouseLeave={e => { e.currentTarget.style.background = rowBg(issue.name, 'issue') || ''; }}>
      {canAct && (
        <td style={{ ...td, width: 34, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
          <input type="checkbox" aria-label={`Select ${issue.name}`} checked={picked.has(issue.name)}
            onChange={() => setPicked(prev => { const next = new Set(prev); if (next.has(issue.name)) next.delete(issue.name); else next.add(issue.name); return next; })} />
        </td>
      )}
      <td style={td}><SeverityCell severity={issue.severity} /></td>
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 12.5 }}>{issue.name}</span>
          {inRemediation[issue.name] && (
            <button title={`In remediation: ${inRemediation[issue.name].title}`}
              onClick={e => { e.stopPropagation(); onOpenRemediation(inRemediation[issue.name].request_id); }}
              style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: sans, whiteSpace: 'nowrap',
                background: isDark ? '#0c1d33' : '#EFF6FF', color: isDark ? '#93c5fd' : '#1D4ED8' }}>In remediation</button>
          )}
        </div>
        <div style={summaryStyle}>{summarize(issue)}</div>
      </td>
      <td style={{ ...td, color: slate, whiteSpace: 'nowrap' }}>{issue.category || '—'}</td>
      <td style={{ ...td, color: slate, whiteSpace: 'nowrap' }}>{issue.first_detected_is_lower_bound ? 'Before ' : ''}{fmtDate(issue.first_detected)}</td>
      <td style={td}>{issue.status ? <StatusPill status={issue.status} /> : <span style={{ color: muted }}>—</span>}</td>
    </tr>
  );
  const resolvedRow = (r: ResolvedIssue) => (
    <tr key={r.name} data-issue-row onClick={() => openPanel({ kind: 'resolved', name: r.name })}
      style={{ cursor: 'pointer', background: rowBg(r.name, 'resolved') }}
      onMouseEnter={e => { if (!rowBg(r.name, 'resolved')) e.currentTarget.style.background = surface2; }}
      onMouseLeave={e => { e.currentTarget.style.background = rowBg(r.name, 'resolved') || ''; }}>
      <td style={td}><SeverityCell severity={r.previous_severity} /></td>
      <td style={td}>
        <div style={{ fontWeight: 600, fontSize: 12.5 }}>{r.name}</div>
        <div style={summaryStyle}>{summarize(r)}</div>
      </td>
      <td style={{ ...td, color: slate, whiteSpace: 'nowrap' }}>{r.category || '—'}</td>
      <td style={{ ...td, color: slate, whiteSpace: 'nowrap' }}>{r.current_severity ?? 'No longer reported'}</td>
      <td style={{ ...td, fontFamily: MONO, fontWeight: 700, fontSize: 11.5, color: isDark ? '#4ade80' : T.grn }}>{fmtImpact(recovered(r))}</td>
      <td style={td}><StatusPill status="resolved" /></td>
    </tr>
  );

  const renderBody = () => {
    const columns = view === 'resolved' ? 6 : canAct ? 6 : 5;
    if (view === 'resolved') {
      const rows = pageSlice(sortedResolved);
      if (!grouped) return rows.map(resolvedRow);
      return groupRows(rows, r => r.category, resolvedRow, columns);
    }
    const rows = pageSlice(sortedIssues);
    if (!grouped) return rows.map(issueRow);
    return groupRows(rows, i => i.category, issueRow, columns);
  };
  function groupRows<R>(rows: R[], categoryOf: (row: R) => string | null, render: (row: R) => React.ReactElement, columns: number) {
    const groups = new Map<string, R[]>();
    rows.forEach(row => {
      const key = categoryOf(row) || 'Other';
      groups.set(key, [...(groups.get(key) || []), row]);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0])).flatMap(([category, groupRowsList]) => [
      <tr key={`group-${category}`}>
        <td colSpan={columns} style={{ ...td, background: isDark ? '#0a0a0a' : '#FBFCFE', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: slate, padding: '8px 14px' }}>
          {category} <span style={{ color: muted, fontWeight: 600 }}>· {groupRowsList.length}</span>
        </td>
      </tr>,
      ...groupRowsList.map(render),
    ]);
  }

  const emptyMessage = () => {
    if (filtersActive) return { title: 'No issues match these filters', body: null, clear: true };
    if (view === 'open') return { title: 'No open issues', body: 'The latest scan found no critical, high or medium findings.', clear: false };
    if (!hasComparison) return { title: 'Needs a second scan', body: 'New and resolved issues are worked out by comparing the latest scan with the one before it.', clear: false };
    if (view === 'new') return { title: 'Nothing new since the last scan', body: `No issue appeared or got worse since ${fmtDate(data.previous_scan_date)}.`, clear: false };
    return { title: 'Nothing resolved since the last scan', body: `Every issue from ${fmtDate(data.previous_scan_date)} is still open.`, clear: false };
  };

  const viewTab = (key: View, label: string, count: number | null) => {
    const active = view === key;
    return (
      <button key={key} onClick={() => changeView(key)} style={{
        background: 'none', border: 'none', borderBottom: `2px solid ${active ? ink : 'transparent'}`, marginBottom: -1,
        padding: '11px 12px', fontSize: 12, fontWeight: 600, color: active ? ink : slate, cursor: 'pointer', fontFamily: sans,
      }}>
        {label}
        {count !== null && (
          <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: isDark ? '#1a1a1a' : '#F1F5F9', color: slate, marginLeft: 6 }}>{count}</span>
        )}
      </button>
    );
  };

  const chip = (active: boolean, label: React.ReactNode, onClick: () => void, key: string) => (
    <button key={key} onClick={onClick} style={{
      fontSize: 11, fontWeight: 600, padding: '6px 11px', borderRadius: 99, cursor: 'pointer', fontFamily: sans,
      display: 'inline-flex', alignItems: 'center', gap: 6,
      border: `1px solid ${active ? ink : line}`, background: active ? ink : surface, color: active ? (isDark ? '#0d0d0d' : '#fff') : slate,
    }}>{label}</button>
  );
  const control: React.CSSProperties = {
    fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `1px solid ${line}`, background: surface, color: ink, fontFamily: sans, cursor: 'pointer',
  };

  const empty = rowCount === 0 ? emptyMessage() : null;

  return (
    <div style={{ fontFamily: sans }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: slate }}>
          {(() => {
            const sameDay = hasComparison && fmtDate(data.scan_date) === fmtDate(data.previous_scan_date);
            const fmt = sameDay ? fmtDateTime : fmtDate;
            return <>
              Critical, high and medium findings from the latest scan · {fmt(data.scan_date)}
              {hasComparison && <> · compared with {fmt(data.previous_scan_date)}</>}
            </>;
          })()}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportView} disabled={!rowCount} style={{ ...topBtn(false, isDark), opacity: rowCount ? 1 : 0.5 }}>Export CSV</button>
          {canAct && issues.length > 0 && (
            <button onClick={() => setRequesting(Array.from(picked))} style={topBtn(true, isDark)}>
              Request remediation{picked.size ? ` (${picked.size})` : ''}
            </button>
          )}
        </div>
      </div>

      {/* AI triage suggestion */}
      {suggestion && (
        <div style={{ ...card(isDark), padding: '14px 16px', marginBottom: 14, borderColor: isDark ? '#3730a3' : '#C7D2FE' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{
              flexShrink: 0, width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isDark ? '#1e1b4b' : '#EEF2FF', color: isDark ? '#a5b4fc' : '#4F46E5', fontSize: 14,
            }}>✦</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: isDark ? '#a5b4fc' : '#4F46E5' }}>Suggested by FluxWatch AI</span>
                <span style={{ fontSize: 11, color: muted }}>{suggestion.finding_count} finding{suggestion.finding_count === 1 ? '' : 's'} in {suggestion.groups.length} group{suggestion.groups.length === 1 ? '' : 's'}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: ink, marginTop: 4 }}>{suggestion.title}</div>
              {suggestion.summary && <div style={{ fontSize: 12.5, color: slate, marginTop: 4, lineHeight: 1.5 }}>{suggestion.summary}</div>}
              {suggestion.groups.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {suggestion.groups.map((g, i) => (
                    <span key={i} title={g.reasoning || undefined} style={{
                      fontSize: 11, padding: '3px 9px', borderRadius: 99, background: surface2, color: slate, border: `1px solid ${line}`,
                    }}>{g.title} · {g.indicator_names.length}</span>
                  ))}
                </div>
              )}
              {canAct && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={() => {
                    setPicked(new Set(suggestion.indicator_names));
                    setFromSuggestion({ id: suggestion.id, title: suggestion.title, summary: suggestion.summary, dueDate: suggestion.suggested_due_date });
                    setRequesting(suggestion.indicator_names);
                  }} style={topBtn(true, isDark)}>Create remediation request</button>
                  <button onClick={() => dismissSuggestion.mutate(suggestion.id)} disabled={dismissSuggestion.isPending}
                    style={{ ...topBtn(false, isDark), opacity: dismissSuggestion.isPending ? 0.6 : 1 }}>
                    {dismissSuggestion.isPending ? 'Dismissing…' : 'Dismiss'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
        <button style={kpiStyle(tileActive('open'))} onClick={() => pickTile('open')}>
          <div style={kpiLabel}>Open issues</div>
          <div style={kpiValue(ink)}>{issues.length}</div>
          <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', gap: 2, marginTop: 10, background: issues.length ? 'transparent' : surface2 }}>
            {SEVERITIES.map(s => openCounts[s] > 0 && <span key={s} style={{ flex: openCounts[s], background: sevColor(s) }} />)}
          </div>
        </button>
        {(['Critical', 'High'] as Severity[]).map(s => (
          <button key={s} style={kpiStyle(tileActive(s), sevColor(s))} onClick={() => pickTile(s)}>
            <div style={kpiLabel}>{s}</div>
            <div style={kpiValue(openCounts[s] ? sevColor(s) : ink)}>{openCounts[s]}</div>
            <div style={kpiNote}>{topCategoryNote(s)}</div>
          </button>
        ))}
        <button style={kpiStyle(tileActive('new'), isDark ? '#93c5fd' : '#1D4ED8')} onClick={() => pickTile('new')}>
          <div style={kpiLabel}>New since last scan</div>
          <div style={kpiValue(hasComparison && newIssues.length ? (isDark ? '#93c5fd' : '#1D4ED8') : ink)}>
            {hasComparison ? (newIssues.length ? `+${newIssues.length}` : '0') : '—'}
          </div>
          <div style={kpiNote}>
            {!hasComparison ? 'Needs a second scan' : worsenedCount ? `${worsenedCount} worsened severity` : 'No severity increases'}
          </div>
        </button>
        <button style={kpiStyle(tileActive('resolved'), isDark ? '#4ade80' : T.grn)} onClick={() => pickTile('resolved')}>
          <div style={kpiLabel}>Resolved since last scan</div>
          <div style={kpiValue(hasComparison && resolved.length ? (isDark ? '#4ade80' : T.grn) : ink)}>{hasComparison ? resolved.length : '—'}</div>
          <div style={kpiNote}>
            {!hasComparison ? 'Needs a second scan' : resolved.length ? `Score impact recovered ${fmtImpact(recoveredTotal)}` : 'Nothing fixed yet'}
          </div>
        </button>
      </div>

      {/* List */}
      <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${line}`, padding: '0 14px' }}>
          {viewTab('open', 'Open', issues.length)}
          {viewTab('new', 'New', hasComparison ? newIssues.length : null)}
          {viewTab('resolved', 'Resolved', hasComparison ? resolved.length : null)}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 14px', borderBottom: `1px solid ${line}`, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 300 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={muted} strokeWidth="2" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
            </svg>
            <input placeholder="Search issues…" value={search} onChange={e => { setSearch(e.target.value); resetPaging(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '7px 11px 7px 31px', fontSize: 12, fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`, borderRadius: 6, outline: 'none' }} />
          </div>
          {chip(!severityFilter, 'All', () => { setSeverityFilter(''); resetPaging(); }, 'all')}
          {SEVERITIES.map(s => chip(
            severityFilter === s,
            <><span style={{ width: 7, height: 7, borderRadius: '50%', background: sevColor(s) }} />{s} {severityCounts[s]}</>,
            () => { setSeverityFilter(severityFilter === s ? '' : s); resetPaging(); },
            s,
          ))}
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); resetPaging(); }} style={control}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value as 'none' | 'category')} style={control}>
            <option value="none">Group: None</option>
            <option value="category">Group: Category</option>
          </select>
        </div>

        {canAct && picked.size > 0 && view !== 'resolved' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderBottom: `1px solid ${line}`,
            background: isDark ? '#0a1628' : T.blueBg, fontSize: 12.5, color: ink, flexWrap: 'wrap' }}>
            <strong>{picked.size} selected</strong>
            <button onClick={() => setRequesting(Array.from(picked))} style={{ background: 'none', border: 'none', color: accent, fontWeight: 600, cursor: 'pointer', fontSize: 12.5, fontFamily: sans, padding: 0 }}>
              Request remediation
            </button>
            <button onClick={() => setPicked(new Set())} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: 12.5, fontFamily: sans, padding: 0 }}>Clear selection</button>
          </div>
        )}

        {empty ? (
          <div style={{ padding: '52px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: ink, marginBottom: 4 }}>{empty.title}</div>
            {empty.body && <div style={{ fontSize: 12.5, color: slate }}>{empty.body}</div>}
            {empty.clear && <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', fontSize: 12.5, fontFamily: sans }}>Clear filters</button>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {canAct && view !== 'resolved' && (() => {
                    const shown = pageSlice(sortedIssues);
                    const all = shown.length > 0 && shown.every(i => picked.has(i.name));
                    return (
                      <th style={{ ...th, width: 34, paddingRight: 0 }}>
                        <input type="checkbox" aria-label="Select all shown issues" checked={all} disabled={!shown.length}
                          onChange={() => setPicked(prev => { const next = new Set(prev); shown.forEach(i => (all ? next.delete(i.name) : next.add(i.name))); return next; })} />
                      </th>
                    );
                  })()}
                  {sortHeader('severity', view === 'resolved' ? 'Was' : 'Severity', 110)}
                  {sortHeader('name', 'Issue')}
                  {sortHeader('category', 'Category')}
                  {view === 'resolved' && <th style={th}>Now</th>}
                  {view === 'resolved' ? sortHeader('impact', 'Recovered', 100) : sortHeader('first', 'First detected', 130)}
                  <th style={{ ...th, width: 100 }}>Status</th>
                </tr>
              </thead>
              <tbody>{renderBody()}</tbody>
            </table>
          </div>
        )}

        {!empty && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', fontSize: 11.5, color: slate, flexWrap: 'wrap', gap: 8 }}>
            <span>
              {grouped
                ? `Showing all ${rowCount}`
                : `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, rowCount)} of ${rowCount}`}
            </span>
            {!grouped && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} style={{ ...control, padding: '5px 10px', opacity: currentPage === 1 ? 0.5 : 1 }}>‹ Prev</button>
                <span>Page {currentPage} of {totalPages}</span>
                <button disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} style={{ ...control, padding: '5px 10px', opacity: currentPage === totalPages ? 0.5 : 1 }}>Next ›</button>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); resetPaging(); }} style={{ ...control, padding: '5px 8px' }}>
                  {[10, 25, 50].map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </span>
            )}
          </div>
        )}
      </div>

      {(selectedIssue || selectedResolved) && (
        <IssuePanel
          issue={selectedIssue}
          resolvedIssue={selectedResolved}
          payload={data}
          vendorId={vendorId}
          vendorName={vendorName}
          isDark={isDark}
          visible={panelVisible}
          onClose={closePanel}
          onOpenScan={scanId => navigate(`/scans/${scanId}/results`)}
          onToast={setToast}
          palette={{ line, ink, slate, muted, surface, surface2, accent }}
          sevColor={sevColor}
          sevBg={sevBg}
          statusStyle={statusStyle}
          remediation={selectedIssue ? inRemediation[selectedIssue.name] ?? null : null}
          onRequestRemediation={canAct && selectedIssue ? () => setRequesting([selectedIssue.name]) : undefined}
          onOpenRemediation={onOpenRemediation}
        />
      )}

      {requesting && (
        <RequestRemediationModal isDark={isDark} vendor={{ id: vendorId, name: vendorName, domain: vendorDomain }} preselected={requesting}
          defaultTitle={fromSuggestion?.title} defaultMessage={fromSuggestion?.summary ?? undefined} defaultDueDate={fromSuggestion?.dueDate}
          triageSuggestionId={fromSuggestion?.id}
          onClose={() => { setRequesting(null); setFromSuggestion(null); }}
          onCreated={id => { setRequesting(null); setFromSuggestion(null); setPicked(new Set()); onOpenRemediation(id); }} />
      )}

      {toast && createPortal(
        <div role="status" style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1400, padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans,
          background: surface, color: ink, border: `1px solid ${line}`, boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.8)' : '0 8px 24px rgba(15,23,42,.12)',
        }}>
          <span style={{ color: T.grn, fontWeight: 700, marginRight: 8 }}>✓</span>{toast}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ─── Detail drawer ───────────────────────────────────────────────── */

function IssuePanel({
  issue, resolvedIssue, payload, vendorId, vendorName, isDark, visible, onClose, onOpenScan, onToast, palette, sevColor, sevBg, statusStyle,
  remediation, onRequestRemediation, onOpenRemediation,
}: {
  issue: Issue | null;
  resolvedIssue: ResolvedIssue | null;
  payload: Payload;
  vendorId: string;
  vendorName: string;
  isDark: boolean;
  visible: boolean;
  onClose: () => void;
  onOpenScan: (scanId: string) => void;
  onToast: (message: string) => void;
  palette: { line: string; ink: string; slate: string; muted: string; surface: string; surface2: string; accent: string };
  sevColor: (s: Severity) => string;
  sevBg: (s: Severity) => string;
  statusStyle: (s: Exclude<Status, null> | 'resolved') => { bg: string; color: string };
  remediation: { request_id: string; title: string; status: string } | null;
  onRequestRemediation?: () => void;
  onOpenRemediation: (requestId: string) => void;
}) {
  const { line, ink, slate, muted, surface, surface2, accent } = palette;
  const panelRef = useRef<HTMLElement>(null);
  const [showRaw, setShowRaw] = useState(false);
  const name = issue?.name ?? resolvedIssue?.name ?? '';

  useEffect(() => { setShowRaw(false); }, [name]);

  // Outside clicks close the drawer; clicks on another issue row are left to the row, which switches issue.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest('[data-issue-row], [role="dialog"], [role="menu"]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const severity = issue?.severity ?? resolvedIssue!.previous_severity;
  const scansConsidered = payload.scans_considered ?? 1;

  const pill = (label: string, bg: string, color: string) => (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>
  );
  const label: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted };
  const metaValue: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, color: ink, marginTop: 4 };
  const section: React.CSSProperties = { padding: '16px 20px', borderBottom: `1px solid ${line}` };
  const btn: React.CSSProperties = {
    fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '8px 12px', borderRadius: 6, border: `1px solid ${line}`, background: surface, color: ink, cursor: 'pointer',
  };

  const severityChange = () => {
    if (!issue) return `${resolvedIssue!.previous_severity} → ${resolvedIssue!.current_severity ?? 'Not reported'}`;
    if (!issue.status) return 'No earlier scan';
    if (issue.status === 'new') return 'New this scan';
    if (issue.previous_severity && issue.previous_severity !== issue.severity) return `${issue.previous_severity} → ${issue.severity}`;
    return 'No change';
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/vendors/${vendorId}?tab=issues&issue=${encodeURIComponent(name)}`;
    try {
      await navigator.clipboard.writeText(url);
      onToast('Issue link copied');
    } catch {
      window.prompt('Copy this link', url);
    }
  };

  const exportIssue = () => {
    const file = `${slug(vendorName)}-${slug(name)}.csv`;
    if (issue) {
      downloadCsv(file, ['Vendor', 'Issue', 'Category', 'Severity', 'Score impact', 'Status', 'First detected', 'Seen in scans', 'Description', 'Recommendation'],
        [[vendorName, issue.name, issue.category, issue.severity, issue.score_impact ?? '', issue.status ? STATUS_LABEL[issue.status] : '', fmtDate(issue.first_detected),
          `${issue.seen_in} of ${scansConsidered}`, issue.description, issue.recommendation]]);
    } else if (resolvedIssue) {
      downloadCsv(file, ['Vendor', 'Issue', 'Category', 'Was', 'Now', 'Impact recovered', 'Resolved in scan', 'Description'],
        [[vendorName, resolvedIssue.name, resolvedIssue.category, resolvedIssue.previous_severity, resolvedIssue.current_severity ?? 'No longer reported',
          fmtNumber(recovered(resolvedIssue)), fmtDate(payload.scan_date), resolvedIssue.description]]);
    }
  };

  const facts = issue ? evidenceFacts(issue.evidence) : [];
  const rawEvidence = issue && issue.evidence != null
    ? (typeof issue.evidence === 'string' ? issue.evidence : JSON.stringify(issue.evidence, null, 2))
    : null;
  const description = issue?.description ?? resolvedIssue?.description;
  const recommendation = issue?.recommendation ?? resolvedIssue?.recommendation;
  const reportScanId = issue ? payload.scan_id : payload.previous_scan_id;

  // Portalled to <body>: the vendor page's wrappers would otherwise become the containing block of this fixed panel.
  return createPortal(
    <aside ref={panelRef} data-issue-panel role="dialog" aria-label={`${name} details`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 1150, background: surface,
      borderLeft: `1px solid ${line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${visible ? 'issuePanelIn' : 'issuePanelOut'} ${PANEL_SLIDE_MS}ms ${visible ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      <style>{`
        @keyframes issuePanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes issuePanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { aside[data-issue-panel] { animation-duration: 1ms !important; } }
      `}</style>

      <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${line}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {pill(severity, sevBg(severity), sevColor(severity))}
            {issue?.status && pill(STATUS_LABEL[issue.status], statusStyle(issue.status).bg, statusStyle(issue.status).color)}
            {resolvedIssue && pill('Resolved', statusStyle('resolved').bg, statusStyle('resolved').color)}
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: ink, marginTop: 10 }}>{name}</div>
        <div style={{ fontSize: 12, color: slate, marginTop: 3 }}>{issue?.category ?? resolvedIssue?.category ?? '—'}</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ ...section, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          {issue ? (
            <>
              <div><div style={label}>Score impact</div><div style={{ ...metaValue, color: sevColor(issue.severity) }}>{fmtImpact(issue.score_impact)}</div></div>
              <div><div style={label}>Severity change</div><div style={metaValue}>{severityChange()}</div></div>
              <div><div style={label}>First detected</div><div style={metaValue}>{issue.first_detected_is_lower_bound ? 'Before ' : ''}{fmtDate(issue.first_detected)}</div></div>
              <div><div style={label}>Seen in</div><div style={metaValue}>{issue.seen_in} of last {scansConsidered} scan{scansConsidered === 1 ? '' : 's'}</div></div>
            </>
          ) : resolvedIssue && (
            <>
              <div><div style={label}>Severity change</div><div style={metaValue}>{severityChange()}</div></div>
              <div><div style={label}>Impact recovered</div><div style={{ ...metaValue, color: isDark ? '#4ade80' : T.grn }}>{fmtImpact(recovered(resolvedIssue))}</div></div>
              <div><div style={label}>Last flagged</div><div style={metaValue}>{fmtDate(payload.previous_scan_date)}</div></div>
              <div><div style={label}>Resolved in</div><div style={metaValue}>{fmtDate(payload.scan_date)} scan</div></div>
            </>
          )}
        </div>

        {issue && issue.history.length > 1 && (
          <div style={section}>
            <div style={label}>Scan history</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginTop: 10, height: 34 }}>
              {issue.history.map(point => (
                <div key={point.scan_id} title={`${fmtDate(point.date)}: ${point.severity ?? 'not flagged'}`} style={{
                  width: 24, borderRadius: 3,
                  height: point.severity ? { Critical: 34, High: 24, Medium: 16 }[point.severity] : 6,
                  background: point.severity ? sevColor(point.severity) : line,
                }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
              {issue.history.map(point => (
                <span key={point.scan_id} style={{ width: 24, fontSize: 9.5, color: muted, textAlign: 'center' }}>{fmtShortDate(point.date)}</span>
              ))}
            </div>
          </div>
        )}

        <div style={section}>
          <div style={label}>What we found</div>
          <p style={{ fontSize: 12.5, lineHeight: 1.65, color: ink, margin: '6px 0 0' }}>{description || 'No description recorded.'}</p>
          {recommendation && (
            <>
              <div style={{ ...label, marginTop: 14 }}>Recommendation</div>
              <div style={{
                marginTop: 8, padding: '10px 12px', borderRadius: 6, fontSize: 12.5, lineHeight: 1.6,
                background: isDark ? '#0c1d33' : '#EFF6FF', border: `1px solid ${isDark ? '#1e3a5f' : '#BFDBFE'}`, color: isDark ? '#93c5fd' : '#1E40AF',
              }}>{recommendation}</div>
            </>
          )}
        </div>

        {issue && (facts.length > 0 || rawEvidence) && (
          <div style={{ ...section, borderBottom: 'none' }}>
            <div style={label}>Evidence</div>
            {facts.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '6px 10px', fontSize: 12, marginTop: 8 }}>
                {facts.map(([k, v]) => (
                  <div key={k} style={{ display: 'contents' }}>
                    <span style={{ color: slate }}>{k}</span>
                    <span style={{ color: ink, wordBreak: 'break-word' }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
            {rawEvidence && (
              <>
                <button onClick={() => setShowRaw(!showRaw)} style={{ background: 'none', border: 'none', padding: 0, marginTop: 10, color: accent, fontWeight: 600, fontSize: 11.5, cursor: 'pointer', fontFamily: sans }}>
                  {showRaw ? 'Hide raw evidence' : 'Show raw evidence'}
                </button>
                {showRaw && (
                  <pre style={{ margin: '8px 0 0', padding: 10, borderRadius: 6, background: surface2, border: `1px solid ${line}`, fontFamily: MONO, fontSize: 11, color: slate, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto' }}>
                    {rawEvidence}
                  </pre>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: '14px 20px', borderTop: `1px solid ${line}`, display: 'grid', gap: 8 }}>
        {issue && remediation && (
          <button onClick={() => onOpenRemediation(remediation.request_id)} style={{ ...btn, padding: 9 }} title={remediation.title}>
            View remediation request
          </button>
        )}
        {issue && !remediation && onRequestRemediation && (
          <button onClick={onRequestRemediation} style={{ ...btn, background: ink, color: isDark ? '#0d0d0d' : '#fff', border: `1px solid ${ink}`, padding: 9 }}>
            Request remediation
          </button>
        )}
        {reportScanId && (
          <button onClick={() => onOpenScan(reportScanId)} style={issue && !remediation && onRequestRemediation ? { ...btn, padding: 9 } : { ...btn, background: ink, color: isDark ? '#0d0d0d' : '#fff', border: `1px solid ${ink}`, padding: 9 }}>
            {issue ? 'View in scan report' : 'View previous scan report'}
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={copyLink} style={{ ...btn, flex: 1 }}>Copy issue link</button>
          <button onClick={exportIssue} style={{ ...btn, flex: 1 }}>Export</button>
        </div>
      </div>
    </aside>,
    document.body,
  );
}
